import React, { useState, FormEvent } from 'react';

// Define the structure for our form data for type safety
interface FormData {
  propertyAddress: string;
  loanStatus: 'current' | 'behind' | 'foreclosure' | '';
  contactName: string;  contactPhone: string;
  contactEmail: string;
}

// Define the component's props, even if empty for now, for future extensibility
interface ForeclosureQuestionnaireProps {}

/**
 * A questionnaire form for homeowners facing potential foreclosure.
 * It collects essential information to help assess their situation and offer assistance.
 * The component manages its own state for form inputs, loading, and submission success,
 * providing a robust and user-friendly experience.
 *
 * @component
 * @example
 * return <ForeclosureQuestionnaire />
 */
const ForeclosureQuestionnaire: React.FC<ForeclosureQuestionnaireProps> = () => {
  const [formData, setFormData] = useState<FormData>({
    propertyAddress: '',
    loanStatus: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Handles changes to form inputs and updates the component's state.
   * @param e - The change event from an input or select element.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevData => ({      ...prevData,
      [name]: value,
    }));
  };

  /**
   * Handles the form submission.
   * Prevents the default form action, simulates an API call, and handles success/error states.
   * @param e - The form submission event.
   */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Simulate an API call to demonstrate loading and success/error states.    // In a real application, this would be a fetch/axios call to a backend endpoint.
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In a real app, you would send formData to your backend here.
      // For example: await api.submitQuestionnaire(formData);      // If the API call fails, throw an error to be caught below.

      console.log('Form Submitted:', formData);
      setIsSubmitted(true);
    } catch (err) {
      console.error("Submission failed:", err);
      setError('There was an error submitting your information. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Render a success message after a successful submission.
  if (isSubmitted) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-2xl mx-auto my-10 text-center animate-fade-in">
          <h2 className="text-3xl font-bold text-green-600 mb-4">Thank You!</h2>
          <p className="text-gray-700 text-lg">Your information has been received. We will review it and contact you shortly to discuss your options.</p>
        </div>
      </div>
    );
  }

  // Main form rendering. Uses Tailwind CSS for a modern, responsive design.
  return (
    <div className="bg-gray-50 min-h-screen flex items-center justify-center p-4">
      <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg w-full max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800">Facing Foreclosure?</h1>
          <p className="text-gray-600 mt-2">We may be able to help. Fill out this confidential form to see your options.</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-6">
            {/* Property Address */}
            <div>
              <label htmlFor="propertyAddress" className="block text-sm font-medium text-gray-700">
                Property Address              </label>
              <input
                type="text"
                id="propertyAddress"
                name="propertyAddress"
                value={formData.propertyAddress}
                onChange={handleChange}
                required
                aria-required="true"
                className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="123 Main St, Anytown, USA"
              />
            </div>            {/* Loan Status */}
            <div>
              <label htmlFor="loanStatus" className="block text-sm font-medium text-gray-700">
                Current Loan Status
              </label>
              <select
                id="loanStatus"
                name="loanStatus"
                value={formData.loanStatus}
                onChange={handleChange}
                required
                aria-required="true"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="" disabled>Select status...</option>
                <option value="current">Current on payments</option>
                <option value="behind">Behind on payments</option>
                <option value="foreclosure">In foreclosure process</option>
              </select>
            </div>
                        <hr className="border-gray-200" />

            <h2 className="text-xl font-semibold text-gray-800">Your Contact Information</h2>

            {/* Contact Name */}
            <div>
              <label htmlFor="contactName" className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <input
                type="text"
                id="contactName"
                name="contactName"
                autoComplete="name"
                value={formData.contactName}
                onChange={handleChange}
                required
                aria-required="true"
                className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Jane Doe"
              />
            </div>

            {/* Contact Phone & Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">
                  Phone Number                </label>
                <input
                  type="tel"
                  id="contactPhone"
                  name="contactPhone"
                  autoComplete="tel"
                  value={formData.contactPhone}
                  onChange={handleChange}
                  required
                  aria-required="true"
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  autoComplete="email"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  required                  aria-required="true"
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="you@example.com"
                />
              </div>
            </div>
          </div>
          
          {error && <p className="mt-4 text-sm text-red-600 text-center">{error}</p>}

          <div className="mt-8">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Submitting...' : 'Get My Options'}            </button>
          </div>
          <p className="mt-4 text-xs text-gray-500 text-center">
            By submitting this form, you agree to be contacted by our representatives. Your information is kept confidential.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ForeclosureQuestionnaire;